import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import { useSidebar } from '@/components/ui/sidebar';
import {
  chordShapes,
  chordsOf,
  DEFAULT_VIEW_MODE,
  displayKey,
  isViewMode,
  renderedContent,
  shapeKey,
  VIEW_MODE_STORAGE_KEY,
  VIEW_MODES,
  type ViewMode,
} from '@/lib/song';
import { cn } from '@/lib/utils';
import { ChordCard } from './chord-card';
import { SetlistRail } from './setlist-rail';
import { SongControls } from './song-controls';
import { SongSheet } from './song-sheet';
import { StageDock } from './stage-dock';

export type ViewerSong = {
  id: string;
  title: string;
  originalKey: string;
  capo: number | null;
  notes: string | null;
  content: string;
  artist: string | null;
  /** Tom escolhido no repertório; `null` = tom original. */
  key: string | null;
};

const MODE_OPTIONS = (Object.keys(VIEW_MODES) as ViewMode[]).map((value) => ({
  value,
  label: VIEW_MODES[value].label,
}));

const readStoredMode = (): ViewMode => {
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return isViewMode(stored) ? stored : DEFAULT_VIEW_MODE;
  } catch {
    return DEFAULT_VIEW_MODE;
  }
};

/** Primeiro ancestral com rolagem (o `<main>` do painel ou, na falta, a página). */
const findScroller = (element: HTMLElement | null): HTMLElement => {
  let parent = element?.parentElement ?? null;

  while (parent) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
};

const isTyping = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  return Boolean(
    element?.closest(
      'input, textarea, select, [contenteditable="true"], [role="dialog"]',
    ),
  );
};

/**
 * Visualizador de cifra (uma música ou um repertório) com os 3 modos: Nítido, Partitura e
 * Palco. Tom, capo, tamanho da letra e rolagem valem para a música atual; ao trocar de
 * música o tom volta ao do repertório, o capo ao da música e a rolagem para o topo.
 */
export const SongViewer: React.FC<{
  songs: ViewerSong[];
  /** Nome do repertório; ausente = música avulsa (sem coluna de músicas). */
  setlistTitle?: string;
  onPrint: () => void;
}> = ({ songs, setlistTitle, onPrint }) => {
  const { open, setOpen } = useSidebar();
  const rootRef = useRef<HTMLDivElement>(null);

  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState<ViewMode>(readStoredMode);
  const [transpose, setTranspose] = useState(0);
  const [capo, setCapo] = useState(0);
  const [fontSize, setFontSize] = useState(20);
  const [showChords, setShowChords] = useState(true);
  const [showDiagrams, setShowDiagrams] = useState(true);
  const [scrolling, setScrolling] = useState(false);
  const [speed, setSpeed] = useState(2);

  const song = songs[Math.min(index, songs.length - 1)]!;
  const config = VIEW_MODES[mode];
  const hasRail =
    Boolean(setlistTitle) && songs.length > 0 && config.setlistPanel === 'left-column';

  // modo foco: o menu recolhe para ícones enquanto a cifra está aberta
  useEffect(() => {
    const wasOpen = open;
    setOpen(false);
    return () => setOpen(wasOpen);
    // só na entrada/saída da tela
    // biome-ignore lint/correctness/useExhaustiveDependencies: efeito de montagem
  }, []);

  // troca de música: tom do repertório, capo da música, rolagem parada e no topo
  // biome-ignore lint/correctness/useExhaustiveDependencies: depende só da música atual
  useEffect(() => {
    setTranspose(0);
    setCapo(song.capo ?? 0);
    setScrolling(false);
    findScroller(rootRef.current).scrollTo({ top: 0 });
  }, [song.id, index]);

  const changeMode = (next: ViewMode) => {
    setMode(next);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, next);
    } catch {
      // modo só não persiste
    }
  };

  const go = useCallback(
    (to: number) => setIndex(Math.max(0, Math.min(songs.length - 1, to))),
    [songs.length],
  );

  const baseKey = song.key || song.originalKey;
  const input = { originalKey: song.originalKey, setlistKey: song.key, transpose, capo };
  const shown = displayKey(input);
  const played = shapeKey(input);
  const content = useMemo(
    () => renderedContent(song.content, input),
    // biome-ignore lint/correctness/useExhaustiveDependencies: `input` é derivado dos campos listados
    [song.content, song.originalKey, song.key, transpose, capo],
  );
  const chords = useMemo(() => chordsOf(content), [content]);

  // rolagem automática: acumula a posição porque `scrollTop` arredonda passos < 1px
  useEffect(() => {
    if (!scrolling) return;

    const scroller = findScroller(rootRef.current);
    let position = scroller.scrollTop;
    let lastSet = position;

    const timer = window.setInterval(() => {
      if (Math.abs(scroller.scrollTop - lastSet) > 2) position = scroller.scrollTop; // usuário rolou
      position += speed * 0.6;
      lastSet = position;
      scroller.scrollTop = position;

      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1) {
        setScrolling(false);
      }
    }, 40);

    return () => window.clearInterval(timer);
  }, [scrolling, speed]);

  // atalhos: ←/→ música, ↑/↓ tom, espaço rolagem
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target) || event.metaKey || event.ctrlKey || event.altKey)
        return;

      if (event.key === 'ArrowRight') go(index + 1);
      else if (event.key === 'ArrowLeft') go(index - 1);
      else if (event.key === 'ArrowUp') setTranspose((value) => value + 1);
      else if (event.key === 'ArrowDown') setTranspose((value) => value - 1);
      else if (event.key === ' ') setScrolling((value) => !value);
      else return;

      event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go, index]);

  if (songs.length === 0) {
    return (
      <p className="py-24 text-center text-muted-foreground">
        Este repertório ainda não tem músicas.
      </p>
    );
  }

  const next = songs[index + 1];

  const controls = (
    <SongControls
      baseKey={baseKey}
      shownKey={shown}
      transpose={transpose}
      onTranspose={setTranspose}
      capo={capo}
      onCapo={setCapo}
      fontSize={fontSize}
      onFontSize={setFontSize}
      scrolling={scrolling}
      onToggleScroll={() => setScrolling((value) => !value)}
      speed={speed}
      onSpeed={setSpeed}
      showChords={showChords}
      onToggleChords={() => setShowChords((value) => !value)}
      showDiagrams={showDiagrams}
      onToggleDiagrams={() => setShowDiagrams((value) => !value)}
      onPrint={onPrint}
    />
  );

  const diagrams = showDiagrams && showChords && chords.length > 0 && (
    <div
      className={cn(
        config.diagrams === 'right-column'
          ? 'grid h-fit grid-cols-2 gap-2 lg:sticky lg:top-[88px]'
          : 'flex flex-wrap gap-2',
      )}
    >
      {chords.map((chord) => (
        <ChordCard
          key={chord}
          name={chord}
          shape={chordShapes[chord]}
          className={config.diagrams === 'strip' ? 'w-[92px]' : undefined}
        />
      ))}
    </div>
  );

  const isStage = mode === 'palco';

  return (
    <div ref={rootRef} className={cn('flex flex-col gap-6', isStage && 'pb-28')}>
      <header
        className={cn(
          'flex flex-col gap-3 print:hidden',
          isStage && 'mx-auto w-full max-w-[1100px]',
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="eyebrow text-accent-ink">
            {setlistTitle ? `Música ${index + 1} de ${songs.length}` : 'Cifra'}
          </span>
          <Segmented<ViewMode>
            value={mode}
            onChange={changeMode}
            options={MODE_OPTIONS}
          />
        </div>

        <h1
          className="display-title text-white"
          style={{ fontSize: isStage ? config.lyrics.titleSize : 36 }}
        >
          {song.title}
        </h1>

        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-muted-foreground">
          {song.artist && <span>{song.artist}</span>}
          <span>
            Tom{' '}
            <strong style={{ color: config.chord.color }} className="font-bold">
              {shown}
            </strong>
          </span>
          {capo > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Icon name="guitar" className="size-3.5 text-accent-ink" />
              Capo na {capo}ª casa · formas de {played}
            </span>
          )}
          {song.notes && <span className="text-faint">{song.notes}</span>}
        </p>

        {/* celular: sem coluna lateral, então anterior/próxima ficam aqui */}
        {setlistTitle && !isStage && (
          <div className="flex items-center gap-2 lg:hidden">
            <button
              type="button"
              disabled={index === 0}
              onClick={() => go(index - 1)}
              className="flex h-9 items-center gap-1.5 rounded-[10px] border border-border px-3 text-[13px] text-white disabled:opacity-30"
            >
              <Icon name="chevron-left" className="size-4" /> Anterior
            </button>
            <button
              type="button"
              disabled={!next}
              onClick={() => go(index + 1)}
              className="flex h-9 items-center gap-1.5 rounded-[10px] border border-border px-3 text-[13px] text-white disabled:opacity-30"
            >
              Próxima <Icon name="chevron-right" className="size-4" />
            </button>
          </div>
        )}
      </header>

      <div
        className={cn(
          'grid gap-8',
          hasRail &&
            config.diagrams === 'right-column' &&
            'lg:grid-cols-[232px_minmax(0,1fr)_196px]',
          hasRail &&
            config.diagrams !== 'right-column' &&
            'lg:grid-cols-[232px_minmax(0,1fr)]',
          !hasRail &&
            config.diagrams === 'right-column' &&
            'lg:grid-cols-[minmax(0,1fr)_196px]',
          isStage && 'mx-auto w-full max-w-[1100px]',
        )}
      >
        {hasRail && (
          <SetlistRail
            className="hidden h-fit lg:sticky lg:top-[88px] lg:flex"
            title={setlistTitle!}
            current={index}
            onSelect={go}
            items={songs.map((item) => ({
              id: item.id,
              title: item.title,
              key: item.key || item.originalKey,
            }))}
          />
        )}

        <div className="flex min-w-0 flex-col gap-8">
          {mode === 'partitura' && (
            <div className="sticky top-[88px] z-20 -mx-1 bg-background/95 px-1 py-2 backdrop-blur print:hidden">
              {controls}
            </div>
          )}

          <SongSheet
            content={content}
            mode={mode}
            showChords={showChords}
            fontSize={fontSize}
          />

          {config.diagrams === 'strip' && diagrams}
        </div>

        {config.diagrams === 'right-column' && diagrams}
      </div>

      {mode === 'nitido' && (
        <div className="fixed bottom-6 left-1/2 z-30 max-w-[calc(100vw-5rem)] -translate-x-1/2 overflow-x-auto rounded-[16px] border border-border-strong bg-background/90 p-2 shadow-[var(--shadow-float)] backdrop-blur print:hidden">
          {controls}
        </div>
      )}

      {isStage && (
        <StageDock
          hasPrevious={index > 0}
          hasNext={Boolean(next)}
          nextTitle={next?.title}
          nextKey={next ? next.key || next.originalKey : undefined}
          onPrevious={() => go(index - 1)}
          onNext={() => go(index + 1)}
        >
          {controls}
        </StageDock>
      )}
    </div>
  );
};
